import React, { useState } from 'react';
import Button from 'src/components/Button';
import FontAwesomeIcon from 'src/components/Icon';
import Editor from 'src/components/Editor/Editor';
import GenericDialogue from 'src/components/Dialogue/Dialogue';
import ConfirmationModal from 'src/components/ConfirmationModal/ConfirmationModal';
import { Specification } from 'src/SharedCustomHooks/UseSpecifications/useSpecifications';
import './SpecificationsRepeater.scss';

const SPEC_TRUNCATE_LENGTH = 260;

const stripHtml = (html: string): string =>
  (html || '').replace(/<[^>]*>/g, '').trim();

interface SpecificationsRepeaterProps {
  specifications: Specification[];
  canEdit: boolean;
  onEdit: (spec: Specification) => void;
  onDelete: (spec: Specification) => void;
}

const SpecificationsRepeater = ({
  specifications,
  canEdit,
  onEdit,
  onDelete,
}: SpecificationsRepeaterProps) => {
  const [expandedSpec, setExpandedSpec] = useState<Specification | null>(null);
  const [specToDelete, setSpecToDelete] = useState<Specification | null>(null);

  const handleConfirmDelete = (): void => {
    if (specToDelete) {
      onDelete(specToDelete);
      setSpecToDelete(null);
    }
  };

  return (
    <>
      <div className="specifications-repeater">
        {specifications.map((spec: Specification) => {
          const isLong: boolean =
            stripHtml(spec.description).length > SPEC_TRUNCATE_LENGTH;

          return (
            <div key={spec.id} className="specification-card">
              <div className="specification-card-header">
                <div className="specification-card-title">{spec.title}</div>
                {canEdit && (
                  <div className="specification-card-actions">
                    <Button
                      color="semantic"
                      onClick={() => onEdit(spec)}
                      label={
                        <FontAwesomeIcon glyph="pencil" weight="light" />
                      }
                    />
                    <Button
                      color="semantic"
                      onClick={() => setSpecToDelete(spec)}
                      label={<FontAwesomeIcon glyph="trash" weight="light" />}
                    />
                  </div>
                )}
              </div>
              <div className="specification-card-body">
                <div
                  className={
                    isLong
                      ? 'specification-card-body-truncated'
                      : 'specification-card-body-full'
                  }
                >
                  <Editor
                    editorState={spec.description}
                    readOnly={true}
                    onEditorStateChange={null}
                    resizable={false}
                  />
                </div>
                {isLong && (
                  <span
                    className="specification-card-read-more"
                    onClick={() => setExpandedSpec(spec)}
                  >
                    Read more
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <GenericDialogue
        isOpen={!!expandedSpec}
        onClose={() => setExpandedSpec(null)}
        title={expandedSpec?.title || 'Specification'}
        maxWidth="80rem"
      >
        <div className="specification-expanded-body">
          <Editor
            editorState={expandedSpec?.description || ''}
            readOnly={true}
            onEditorStateChange={null}
            resizable={false}
          />
        </div>
      </GenericDialogue>

      {specToDelete && (
        <ConfirmationModal
          title="Delete Specification"
          message={
            <>
              Are you sure you want to delete{' '}
              <strong>{specToDelete.title}</strong>?
            </>
          }
          warningMessage="This action cannot be undone."
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={handleConfirmDelete}
          onCancel={() => setSpecToDelete(null)}
          variant="danger"
        />
      )}
    </>
  );
};

export default SpecificationsRepeater;
